import {useMemo, useState} from 'react'

import styles from '../marketplace.module.css'

import {Button, Label, NavList} from '@primer/react'
import {Dialog} from '@primer/react/drafts'
import {AppsIcon, CopilotIcon, FlameIcon, PlayIcon, PlusIcon} from '@primer/octicons-react'
import {useSearchParams} from '@github-ui/use-navigate'
import type {Category} from '../types'

type Props = {
  categories: {
    apps: Category[]
    actions: Category[]
  }
}

function Menu({categories}: Props) {
  const [searchParams] = useSearchParams()

  const currentNavItem = useMemo(() => {
    if (searchParams.size === 0) {
      return 'featured'
    } else if (searchParams.get('type') === 'apps' && searchParams.get('copilot_app') === 'true') {
      return 'copilot'
    } else if (searchParams.has('category')) {
      return searchParams.get('category') || ''
    }
    return ''
  }, [searchParams])

  const currentType = useMemo(() => {
    if (searchParams.has('type') && !searchParams.has('copilot_app')) {
      if (searchParams.get('type') === 'apps') {
        return 'apps'
      } else if (searchParams.get('type') === 'actions') {
        return 'actions'
      }
    }
    return ''
  }, [searchParams])

  return (
    <NavList>
      <NavList.Item href="/marketplace" {...(currentNavItem === 'featured' ? {'aria-current': 'page'} : {})}>
        <NavList.LeadingVisual>
          <FlameIcon />
        </NavList.LeadingVisual>
        Featured
      </NavList.Item>
      <NavList.Item
        href="/marketplace?type=apps&copilot_app=true"
        {...(currentNavItem === 'copilot' ? {'aria-current': 'page'} : {})}
      >
        <NavList.LeadingVisual>
          <CopilotIcon />
        </NavList.LeadingVisual>
        Copilot
        <NavList.TrailingVisual>
          <Label variant="success">Beta</Label>
        </NavList.TrailingVisual>
      </NavList.Item>
      <NavList.Item defaultOpen={currentType === 'apps'}>
        <NavList.LeadingVisual>
          <AppsIcon />
        </NavList.LeadingVisual>
        Apps
        <NavList.SubNav>
          <NavList.Item
            href={`/marketplace?type=apps`}
            {...(currentType === 'apps' && currentNavItem === '' ? {'aria-current': 'page'} : {})}
          >
            All apps
          </NavList.Item>
          {categories.apps.map(category => (
            <NavList.Item
              key={`apps-category-${category.slug}`}
              href={`/marketplace?type=apps&category=${category.slug}`}
              {...(currentType === 'apps' && currentNavItem === category.slug ? {'aria-current': 'page'} : {})}
            >
              {category.name}
            </NavList.Item>
          ))}
        </NavList.SubNav>
      </NavList.Item>
      <NavList.Item defaultOpen={currentType === 'actions'}>
        <NavList.LeadingVisual>
          <PlayIcon />
        </NavList.LeadingVisual>
        Actions
        <NavList.SubNav>
          <NavList.Item
            href={`/marketplace?type=actions`}
            {...(currentType === 'actions' && currentNavItem === '' ? {'aria-current': 'page'} : {})}
          >
            All actions
          </NavList.Item>
          {categories.actions.map(category => (
            <NavList.Item
              key={`actions-category-${category.slug}`}
              href={`/marketplace?type=actions&category=${category.slug}`}
              {...(currentType === 'actions' && currentNavItem === category.slug ? {'aria-current': 'page'} : {})}
            >
              {category.name}
            </NavList.Item>
          ))}
        </NavList.SubNav>
      </NavList.Item>
      <NavList.Divider />
      <NavList.Item href="/marketplace/new">
        <NavList.LeadingVisual>
          <PlusIcon />
        </NavList.LeadingVisual>
        Create a new extension
      </NavList.Item>
    </NavList>
  )
}

export default function MarketplaceNavigation({categories}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const onMenuClose = () => setIsMenuOpen(false)

  return (
    <>
      <div className="hide-sm hide-md">
        <Menu categories={categories} />
      </div>

      <Button block className="hide-lg hide-xl" onClick={() => setIsMenuOpen(true)}>
        Menu
      </Button>
      {isMenuOpen && (
        <Dialog title="Menu" onClose={onMenuClose} position={{narrow: 'bottom'}}>
          <div className={styles['negative-margin']}>
            <Menu categories={categories} />
          </div>
        </Dialog>
      )}
    </>
  )
}

try{ Menu.displayName ||= 'Menu' } catch {}
try{ MarketplaceNavigation.displayName ||= 'MarketplaceNavigation' } catch {}